import React, { useContext } from 'react';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { CardContent, CardHeader, Theme } from '@material-ui/core';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FormikHelpers } from 'formik';
import { shallowEqual, useSelector } from 'react-redux';

import {
  CONTRACT_SIDEBAR_WIDTH,
  ContractBuilderSidebar,
} from 'src/components/Contracts/ContractBuilderSidebar';
import {
  signaturePageContentWidth,
  SignaturePageContent,
} from 'src/components/Signature/SignaturePageContent';
import { NonHoverBorder, white } from 'src/theme/colors';
import { BoxDragPreview } from 'src/components/Signature/BoxDragPreview';
import { Callout } from 'src/components/Callout';
import { InformationCircleIcon } from 'src/components/Icons';
import MemoBaseTypography from 'src/components/Text/BaseTypography';
import { LinkButton } from 'src/components/Buttons';
import {
  CONTRACTS_PAGE,
  FormBodyAPI,
  MOBILE_APP_BAR_HEIGHT,
} from 'src/constants';
import { ContractFileDropzone } from 'src/components/Contracts/ContractFileDropzone';
import { BaseTextField } from 'src/components/TextField';
import { ContractTemplateForm } from 'src/components/Contracts';
import { RegularCardBox } from 'src/components/Cards/RegularCardBox';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { RootState } from 'src/store';
import { LoadingWrapper } from 'src/components/Loading';
import { useSignatureRequestComponent } from 'src/components/Signature/SignatureSidebar/useSignatureRequestComponent';

const useStyles = makeStyles((theme: Theme) => ({
  contentContainer: {
    width: '100%',
    maxWidth: `calc(100% - ${CONTRACT_SIDEBAR_WIDTH}px)`,
  },
  clientContentContainer: {
    [theme.breakpoints.down('sm')]: {
      height: '100vh',
      overflow: 'auto',
      paddingTop: MOBILE_APP_BAR_HEIGHT,
    },
  },
  content: {
    width: '100%',
    margin: '0 auto',
    maxWidth: signaturePageContentWidth,
    paddingTop: theme.spacing(5),
  },
  callout: {
    backgroundColor: white,
    marginBottom: theme.spacing(5),
    border: `1px solid ${NonHoverBorder}`,
    borderRadius: theme.shape.borderRadius,
  },
  calloutContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  calloutIcon: {
    fontSize: 14,
  },
  card: {
    boxShadow: 'none',
    borderColor: NonHoverBorder,
    marginBottom: theme.spacing(5),
  },
  cardHeader: {
    borderBottom: `1px solid ${NonHoverBorder}`,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

/**
 * Hook for the contract template creation "FormPage" component.
 *
 * @returns {object} An object containing:
 *   - {object} validationScheme: Form validation rules.
 *   - {function} getSubmitValues: A function to retrieve form submission values.
 *   - {JSX.Element} FormRenderer: The form's UI components, including the configuration form, PDF, and draggable signature components.
 */
export const useContractBuilderAndSignerForm = () => {
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const { SignatureComponentsForm } = useSignatureRequestComponent();

  // For clients, no validation scheme is applied because no inline errors are shown.
  // Instead, validation will be handled in the save callback.
  const validationScheme = isClient
    ? Yup.object({})
    : Yup.object().shape({
        title: Yup.string().required('Contract template must have a name'),
        fileKey: Yup.string().required(
          'Contract template must have a PDF file',
        ),
      });

  const getSubmitValues = async () => ({});

  const FormRenderer = ({
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
  }: FormBodyAPI & FormikHelpers<ContractTemplateForm>) => {
    const classes = useStyles();
    const { setPlaceholderElement } = useContext(ContractBuilderContext);
    const { pageLoading, pageImages, completeRenderPageImages } = useSelector(
      (state: RootState) => ({
        pageLoading: state.signaturePage.loading,
        pageImages: state.signaturePage.pageImages,
        completeRenderPageImages: state.signaturePage.completeRenderPageImages,
      }),
      shallowEqual,
    );

    return (
      <DndProvider backend={HTML5Backend}>
        <BoxDragPreview />
        <div>
          <div
            className={classNames({
              [classes.contentContainer]: !isClient,
              [classes.clientContentContainer]: isClient,
            })}
          >
            <div
              className={classNames({
                [classes.content]: !isClient,
              })}
            >
              {!isClient && (
                <>
                  <Callout hideIcon className={classes.callout}>
                    <div className={classes.calloutContent}>
                      <InformationCircleIcon className={classes.calloutIcon} />
                      <MemoBaseTypography fontType="13Regular">
                        Want to use an existing contract template? Select one
                        from the{' '}
                        <LinkButton
                          fontType="13Regular"
                          text="templates page."
                          suffixIcon={null}
                          toPath={CONTRACTS_PAGE.path}
                        />
                      </MemoBaseTypography>
                    </div>
                  </Callout>
                  <RegularCardBox className={classes.card}>
                    <CardHeader
                      className={classes.cardHeader}
                      title={
                        <MemoBaseTypography fontType="15Medium">
                          Contract setup
                        </MemoBaseTypography>
                      }
                    />

                    <CardContent className={classes.cardContent}>
                      <ContractFileDropzone name="fileKey" />
                      <BaseTextField
                        label="Template name"
                        variant="outlined"
                        sizeVariant="medium"
                        value={values.title}
                        fullWidth
                        name="title"
                        error={Boolean(touched?.title && errors?.title)}
                        helperText={(touched.title && errors.title) || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </CardContent>
                  </RegularCardBox>
                </>
              )}
              <LoadingWrapper
                isLoading={pageLoading && !completeRenderPageImages}
                hideContentWhileLoading
              >
                {!pageLoading && pageImages.length > 0 && (
                  <SignaturePageContent
                    forContracts
                    onSavePlaceholderElementReference={setPlaceholderElement}
                    onPageSave={() => null} // TODO: Implement method to save client-signed contract
                  >
                    <SignatureComponentsForm />
                  </SignaturePageContent>
                )}
              </LoadingWrapper>
            </div>
          </div>
          {!isClient && <ContractBuilderSidebar />}
        </div>
      </DndProvider>
    );
  };

  return { validationScheme, getSubmitValues, FormRenderer };
};
